<script setup>
import { computed, ref, onMounted, getCurrentInstance, watch, useTemplateRef, onBeforeUnmount } from 'vue'
import _ from 'lodash'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from "@kyvg/vue3-notification"
import utils from '@/utils'
import IconBase from '@/components/icon/IconBase.vue'
import IconSettings from '@/components/icon/IconSettings.vue'
import IconChevronUpDown from '@/components/icon/IconChevronUpDown.vue'
import PlatformDetailsCustom from '@/components/PlatformDetailsCustom'
import Alert from './Atoms/Alert.vue'
import DropDownMenu from './Atoms/DropDownMenu.vue'
import DropDownMenuItem from './Atoms/DropDownMenuItem.vue'
import FormInput from './Atoms/FormInput.vue'
import FormButton from './Atoms/FormButton.vue'

const instance = getCurrentInstance()
const root = instance.proxy.$root
const route = useRoute()
const router = useRouter()
const store = useStore()
const { notify } = useNotification()

// Props
const props = defineProps({
	close: {
		type: Function,
	}
})

// Emits
const emit = defineEmits({
	'close-modal': () => true
})

// Rafs
const server = ref({
	text: null,
	value: null,
})
const key = ref(null)
const mode = ref(null)
const latency = ref(null)
const port = ref(null)
const passphrase = ref(null)
const errors = ref({
	response: null,
	server: false,
})

// Computed
const isPlatformSelected = computed(() => store.getters['Destination/isPlatformSelected'])
const selectedPlatform = computed(() => store.getters['Destination/selectedPlatform'])
const isCustomPlatform = computed(() => store.getters['Destination/isCustomPlatform'])
const isDisabled = computed(() => store.getters['Destination/isDisabled'])

// Watch
watch(isPlatformSelected, () => {
	clear()
})

watch(key, () => {
	store.dispatch('Destination/setKey', key.value)
})

watch(server, () => {
	errors.value.server = false
	if (!selectedPlatform.value?.servers) store.dispatch('Destination/setServer', server.value.value)
}, {deep: true})

watch(mode, () => {
	if (mode.value === 'srt') store.dispatch('Destination/setMode', mode.value)
})

watch(latency, () => {
	if (mode.value === 'srt') store.dispatch('Destination/setLatency', latency.value)
})

watch(port, () => {
	if (mode.value === 'srt') store.dispatch('Destination/setPort', port.value)
})

watch(passphrase, () => {
	if (mode.value === 'srt') store.dispatch('Destination/setPassphrase', passphrase.value)
})

const save = async () => {
	try {
		await store.dispatch('Destination/createDestination')
		setTimeout(() => {
			store.dispatch('Destination/getDestinations', true) // force refresh after create
		}, 1000)
		emit('close-modal')
	} catch (error) {
		console.log(error)
		errors.value.response = error
	}
}

const cancel = () => {
	emit('close-modal')
}

const clear = () => {
	server.value = {
		text: null,
		value: null,
	}
	key.value = null
	mode.value = null
	latency.value = null
	port.value = null
	passphrase.value = null
}

const selectServer = (item) => {
	server.value = item
	store.dispatch('Destination/setServer', server.value.value)
}

const setCustomPlatform = (item) => {
	server.value.value = item.server
	key.value = item.key
	// console.log(item);
	if (item?.mode === 'srt') {
		mode.value = 'srt'
		latency.value = item.latency
		port.value = item.port
		passphrase.value = item.passphrase
	}
	store.dispatch('Destination/setPidMapping', item?.mode === 'srt'
		? { pids: item.pids, pmtPid: item.pmtPid, valid: item.srtValid !== false }
		: { pids: '', pmtPid: '', valid: true })
}

const validate = (type) => {
	if (type === 'server' && server.value?.value) {
		const { valid } = utils.validateURL(server.value?.value, { validatePathname: false })
		valid
			? errors.value[type] = false
			: errors.value[type] = true
	}
}

</script>

<template>
	<section class="platform-details lg:absolute w-full lg:w-[408px] bg-surface-2 text-surface-9"
		:class="{ 'show-details': isPlatformSelected }">
		<header class="platform-details-header flex justify-center items-center bg-surface-4 h-16">
			<img v-if="!isCustomPlatform && selectedPlatform" width="100" :src="selectedPlatform.icon"
				:alt="selectedPlatform.name" />
			<icon-base v-else width-view-box="24" height-view-box="24" iconColor="none" width="24" height="24"
				class="text-white">
				<icon-settings />
			</icon-base>
		</header>
		<main class="px-4">
			<section>
				<Alert v-if="errors.response" type="error" class="mt-3">
					<div>{{ errors.response }}</div>
				</Alert>
			</section>
			<section class="!pt-4" v-if="selectedPlatform">
				<h3 class="text-sm !mb-2">streaming server</h3>
				<template v-if="selectedPlatform.servers && selectedPlatform.servers.length > 1">
					<DropDownMenu type="flat">
						<template #toggle-button>
							{{ server && server.text ? server.text : 'Select one' }}
							<icon-base class="!ml-1">
								<icon-chevron-up-down />
							</icon-base>
						</template>
						<template #menu-items>
							<DropDownMenuItem @click="selectServer(item)" :key="item.id" v-for="item in selectedPlatform.servers">
								{{ item.text }}
							</DropDownMenuItem>
						</template>
					</DropDownMenu>
				</template>
				<template v-else-if="selectedPlatform.customServer">
					<FormInput v-model="server.value"
						:placeholder="selectedPlatform.serverInputPlaceholder || 'rtmp://broadcaster_addr/'"
						@blur="validate('server')" :isError="errors.server"
						:helper="errors.server ? 'Invalid destination ingest address' : null" />
				</template>
				<FormInput class="!mt-3" v-model="key" placeholder="#streaming_key" label="streaming key" />
			</section>
			<PlatformDetailsCustom v-else-if="isCustomPlatform" @platform-details="setCustomPlatform" />
			<section class="mt-6 flex flex-col gap-y-2">
				<FormButton :disabled="isDisabled" @click="save" class="justify-center">
					Create Destination
				</FormButton>
				<FormButton type="secondary" @click="cancel" class="justify-center">
					Cancel
				</FormButton>
			</section>
		</main>
	</section>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.platform-details {
	top: 0;
	margin: auto;
	height: 100%;
	-webkit-transition: opacity 0.3s linear, right 0.3s ease-out;
	-moz-transition: opacity 0.3s linear, right 0.3s ease-out;
	-o-transition: opacity 0.3s linear, right 0.3s ease-out;
	transition: opacity 0.3s linear, left 0.3s ease-out;
	left: 0;
	z-index: -1;
	border-left: 1px solid var(--c-dark-3);
	border-right: 1px solid var(--c-dark-3);
}

.show-details.platform-details {
	left: 516px;
	-webkit-transition: opacity 0.3s linear, right 0.3s ease-out;
	-moz-transition: opacity 0.3s linear, right 0.3s ease-out;
	-o-transition: opacity 0.3s linear, right 0.3s ease-out;
	transition: opacity 0.3s linear, left 0.3s ease-out;
}
</style>